import React from "react";
import { Box } from "@mui/material";
import SabiasQue from "../SabiasQue";

// Template25: Página de contenido con imagen grande, bloques y SabiasQue (Kioto: Kiyomizudera)
// Contenido desde page.texts, page.images y page.canvasImages (servidor).
const Template25 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const isSmallScreen = window.innerWidth <= 600;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const parseSabiasQueItem = (html) => {
		if (!html) return null;
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const p = doc.querySelector("p");
		if (!p) return null;
		const text = (p.textContent ?? p.innerText ?? "").trim();
		const emojiMatch = text.match(/^([^\s]+)\s+(.*)/s);
		if (emojiMatch) return { icono: emojiMatch[1], texto: emojiMatch[2].trim() };
		return null;
	};

	const headerText = page?.texts?.[0]?.content;
	const introText = page?.texts?.[1]?.content;
	const titleText = page?.texts?.[2]?.content;
	const templeTitle = page?.texts?.[3]?.content;
	const templeText = page?.texts?.[4]?.content;
	const cornersTitle = page?.texts?.[5]?.content;
	const cornersText = page?.texts?.[6]?.content;
	const streetsTitle = page?.texts?.[7]?.content;
	const streetsText = page?.texts?.[8]?.content;
	const infoText = page?.texts?.[9]?.content;
	const sabiasQue1 = parseSabiasQueItem(page?.texts?.[10]?.content);
	const sabiasQue2 = parseSabiasQueItem(page?.texts?.[11]?.content);
	const sabiasQue3 = parseSabiasQueItem(page?.texts?.[12]?.content);
	const sabiasQue4 = parseSabiasQueItem(page?.texts?.[13]?.content);
	const starbucksStickerText = page?.texts?.[14]?.content;

	const mainImage = getImageSrc("kiyomizudera-main");
	const viewImage = getImageSrc("kiyomizudera-view");
	const sannenzakaImage = getImageSrc("sannenzaka");
	const starbucksImage = getImageSrc("starbucks");
	const logoImage = getCanvasImageUrl("logo");
	const sticker1 = getCanvasImageUrl("sticker-1");
	const sticker2 = getCanvasImageUrl("sticker-2");

	const contentColor = textColor || "#444";

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
		color: contentColor,
	};

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "0px !important",
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: headerBackgroundColor || "#5ba374",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
					fontSize: "14px",
					fontWeight: 800,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{/* IMAGEN PORTADA */}
			<Box data-block-id="image-0" sx={{ position: "relative" }}>
				{mainImage && (
					<Box
						component="img"
						src={mainImage}
						alt="Kiyomizudera"
						sx={{
							width: "100%",
							height: 200,
							objectFit: "cover",
						}}
					/>
				)}
				{titleText && (
					<Box
						component="h1"
						sx={{
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							fontSize: isSmallScreen ? "28px" : "32px",
							textAlign: "center",
							position: "absolute",
							bottom: "0",
							margin: "10px",
							left: "20px",
							color: "#fff",
							textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
						}}
						dangerouslySetInnerHTML={{ __html: titleText }}
					/>
				)}
				{sticker1 && (
					<Box
						component="img"
						src={sticker1}
						alt="Kiyomizudera"
						sx={{
							width: "90px",
							position: "absolute",
							bottom: "60px",
							left: "20px",
						}}
					/>
				)}
			</Box>

			<Box
				data-block-id="text-1"
				sx={{
					padding: "10px 12px",
					paddingBottom: "5px",
					position: "relative",
					marginTop: "-4px",
				}}
			>
				{introText && (
					<Box
						component="p"
						sx={{
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							fontSize: "13px",
							color: "#333",
							margin: "0 !important",
							padding: 0,
							marginBottom: "0px !important",
						}}
						dangerouslySetInnerHTML={{ __html: introText }}
					/>
				)}

				{/* BLOQUES */}
				<Box
					data-block-id="text-2"
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: "9px",
						marginTop: "10px",
					}}
				>
					{/* BLOQUE 1: Templo Kiyomizudera */}
					<Box sx={{ display: "flex", gap: "16px", position: "relative" }}>
						{viewImage && (
							<Box
								component="img"
								src={viewImage}
								alt="Kiyomizudera"
								sx={{
									width: "40%",
									height: "110px",
									objectFit: "cover",
									borderRadius: "12px",
								}}
							/>
						)}
						{sabiasQue1 && (
							<Box sx={{ position: "absolute", top: "20px", left: "34%" }}>
								<SabiasQue
									icono={sabiasQue1.icono}
									texto={sabiasQue1.texto}
								/>
							</Box>
						)}
						{sabiasQue2 && (
							<Box sx={{ position: "absolute", top: "60px", left: "34%" }}>
								<SabiasQue
									icono={sabiasQue2.icono}
									texto={sabiasQue2.texto}
								/>
							</Box>
						)}
						<Box sx={{ width: "60%" }}>
							{templeTitle && (
								<Box
									component="h3"
									sx={{
										fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
										fontSize: "14px",
										fontWeight: 700,
										margin: 0,
										textTransform: "uppercase",
									}}
									dangerouslySetInnerHTML={{ __html: templeTitle }}
								/>
							)}
							{templeText && (
								<Box
									component="p"
									sx={{
										fontSize: "13px",
										color: contentColor,
										fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
										marginTop: "0px !important",
										marginBottom: "0px !important",
									}}
									dangerouslySetInnerHTML={{ __html: templeText }}
								/>
							)}
						</Box>
					</Box>

					{/* BLOQUE 2: Rincones */}
					<Box sx={{ display: "flex", gap: "16px", position: "relative" }}>
						<Box sx={{ width: "100%" }}>
							<Box
								component="p"
								sx={{
									...estiloP,
									padding: "20px",
									backgroundColor: "#d9e6de",
									borderRadius: "12px",
								}}
							>
								{sticker2 && (
									<Box
										component="img"
										src={sticker2}
										alt="Kiyomizudera"
										sx={{
											width: isSmallScreen ? "105px" : "115px",
											float: "right",
											marginRight: "-34px",
											marginTop: "-25px",
										}}
									/>
								)}
								{cornersTitle && (
									<Box
										component="h3"
										sx={{
											...estiloH3,
											marginTop: "0px !important",
											marginBottom: "0px !important",
										}}
										dangerouslySetInnerHTML={{ __html: cornersTitle }}
									/>
								)}
								{cornersText && (
									<Box
										component="span"
										dangerouslySetInnerHTML={{ __html: cornersText }}
									/>
								)}
							</Box>
						</Box>
					</Box>

					{/* BLOQUE 3: Sannenzaka */}
					<Box sx={{ display: "flex", gap: "16px", position: "relative" }}>
						{sannenzakaImage && (
							<Box
								component="img"
								src={sannenzakaImage}
								alt="Sannenzaka"
								sx={{
									width: "40%",
									height: "110px",
									objectFit: "cover",
									borderRadius: "12px",
								}}
							/>
						)}
						{sabiasQue3 && (
							<Box sx={{ position: "absolute", bottom: "20px", left: "34%" }}>
								<SabiasQue
									icono={sabiasQue3.icono}
									texto={sabiasQue3.texto}
								/>
							</Box>
						)}
						{sabiasQue4 && (
							<Box sx={{ position: "absolute", bottom: "60px", left: "34%" }}>
								<SabiasQue
									icono={sabiasQue4.icono}
									texto={sabiasQue4.texto}
								/>
							</Box>
						)}
						<Box sx={{ width: "60%" }}>
							{streetsTitle && (
								<Box
									component="h3"
									sx={estiloH3}
									dangerouslySetInnerHTML={{ __html: streetsTitle }}
								/>
							)}
							{streetsText && (
								<Box
									component="p"
									sx={{
										...estiloP,
										marginTop: "0px !important",
										marginBottom: "0px !important",
									}}
									dangerouslySetInnerHTML={{ __html: streetsText }}
								/>
							)}
						</Box>
					</Box>

					{/* BLOQUE 4: Starbucks e Info */}
					<Box
						sx={{
							display: "flex",
							gap: "12px",
							alignItems: "flex-end",
							marginTop: 0,
						}}
					>
						<Box sx={{ position: "relative", width: "32%" }}>
							{starbucksStickerText && (
								<Box
									sx={{
										position: "absolute",
										bottom: isSmallScreen ? "30px" : "30px",
										left: "44px",
										backgroundColor: "#fff8db",
										border: "2px solid #f5c400",
										borderRadius: "16px 16px 16px 0px",
										padding: "10px 12px",
										fontSize: "11px",
										fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
										color: contentColor,
										boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
										maxWidth: "200px",
										zIndex: 6,
									}}
								>
									{starbucksStickerText}
								</Box>
							)}
							{starbucksImage && (
								<Box
									component="img"
									src={starbucksImage}
									alt="Starbucks"
									sx={{
										width: "100%",
										maxWidth: "100px",
										position: "absolute",
										bottom: "-10px",
										left: "-40px",
										zIndex: 5,
									}}
								/>
							)}
						</Box>

						<Box
							sx={{
								width: "70%",
								backgroundColor: "#d9e6de",
								border: "2px solid #5ba374",
								borderRadius: "12px",
								padding: "8px 16px",
								fontSize: "13px",
								color: contentColor,
								textAlign: "center",
								position: "relative",
							}}
						>
							{infoText && (
								<Box
									component="p"
									sx={{
										...estiloP,
										marginTop: "0px !important",
										marginBottom: "0px !important",
									}}
									dangerouslySetInnerHTML={{ __html: infoText }}
								/>
							)}
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

Template25.thumbnail = "/images/template-1.png";

export default Template25;
